import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { getModelRunsByCustomer } from '../api/projects';
import { ModelResults } from '../features/model/domain/types';
import { ImpactData, extractImpactFromModelRun } from '../features/model/domain/types';

interface ImpactDataResult {
    projectImpacts: Record<string, ImpactData>;
    totalImpact: ImpactData | null;
}

export function useImpactData(customerId: string | null | undefined) {
    const { token } = useAuth();

    return useQuery<ImpactDataResult, Error>({
        queryKey: ['impactData', customerId],
        queryFn: async () => {
            if (!token || !customerId) return { projectImpacts: {}, totalImpact: null };

            try {
                const modelRuns = await getModelRunsByCustomer(customerId, token) as ModelResults[];

                // Group runs by project_id
                const projectRuns: Record<string, ModelResults[]> = {};
                modelRuns.forEach(run => {
                    if (run.project_id && run.status === 'completed') {
                        if (!projectRuns[run.project_id]) {
                            projectRuns[run.project_id] = [];
                        }
                        projectRuns[run.project_id].push(run);
                    }
                });

                // Get latest run for each project
                const projectImpacts: Record<string, ImpactData> = {};
                let totalDALYs = 0;
                let totalPDFs = 0;
                let totalEconomic = 0;
                let totalCapacity = 0;
                let totalGenerationMwh = 0;

                Object.entries(projectRuns).forEach(([projectId, runs]) => {
                    // Sort runs by date (newest first)
                    const sortedRuns = [...runs].sort((a, b) => {
                        const dateA = new Date(a.run_date || a.updated_at || 0).getTime();
                        const dateB = new Date(b.run_date || b.updated_at || 0).getTime();
                        return dateB - dateA;
                    });

                    if (sortedRuns.length > 0) {
                        const impactData = extractImpactFromModelRun(sortedRuns[0]);
                        if (impactData) {
                            projectImpacts[projectId] = impactData;

                            // Add to totals
                            totalDALYs += impactData.dalys;
                            totalPDFs += impactData.pdfs;
                            totalEconomic += impactData.economic;
                            totalCapacity += impactData.mwCapacity;
                            totalGenerationMwh += impactData.generationMwh;
                        }
                    }
                });

                const totalImpact: ImpactData = {
                    projectId: 'portfolio',
                    dalys: totalDALYs,
                    pdfs: totalPDFs,
                    economic: totalEconomic,
                    mwCapacity: totalCapacity,
                    generationMwh: totalGenerationMwh,
                };

                return { projectImpacts, totalImpact };
            } catch (err) {
                console.error('Failed to fetch impact data:', err);
                throw err;
            }
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
        enabled: !!token && !!customerId,
    });
}